import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  FileText,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react'
import { VendorModal } from '@/components/vendor-modal'
import { apiService } from '@/lib/api'
import { useAuth } from '@/contexts/auth-context'
import { PageLayout } from '@/components/page-layout'
import { Skeleton } from '@/components/ui/skeleton'
import { getApiUrl, getCompanyId, getTenantId } from '@/lib/config'
import { TranslatedText } from '@/components/translated-text'
import { useTranslation, useTranslatedText } from '@/hooks/useTranslation'
import { useToast } from '@/hooks/use-toast'

interface Vendor {
  id: string
  name: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  taxNumber?: string
  isActive: boolean
  companyId: string
  createdAt: string
  updatedAt: string
}

export default function VendorsPage() {
  const { user } = useAuth()
  const { tSync } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const searchPlaceholder = useTranslatedText("Search vendors by name, email, phone, or tax number...")

  // Get company ID using config function
  const firstCompanyId = (() => {
    try {
      return getCompanyId()
    } catch {
      return 'seed-company-1'
    }
  })()

  // Get tenant ID using config function
  const tenantId = (() => {
    try {
      return getTenantId()
    } catch {
      return user?.tenantId || 'tenant_demo'
    }
  })()

  // Fetch vendors
  const { data: vendorsData, isLoading: vendorsLoading, error: vendorsError } = useQuery({
    queryKey: ['vendors', firstCompanyId],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const response = await fetch(
        getApiUrl(`api/enhanced-transaction-processing/vendors/${firstCompanyId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          }
        }
      )
      
      if (!response.ok) {
        throw new Error(`Failed to fetch vendors: ${response.statusText}`)
      }
      
      const data = await response.json()
      return data.success ? data.data : []
    },
    enabled: !!firstCompanyId,
    retry: 2
  })

  const vendors: Vendor[] = vendorsData || []

  // Filter vendors based on search query
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.phone?.includes(searchQuery) ||
    vendor.taxNumber?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Create vendor mutation
  const createVendorMutation = useMutation({
    mutationFn: async (vendorData: any) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const response = await fetch(
        getApiUrl('api/enhanced-transaction-processing/vendors'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          },
          body: JSON.stringify(vendorData)
        }
      )
      const data = await response.json()
      if (!data.success) {
        throw new Error(data.error || 'Failed to create vendor')
      }
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', firstCompanyId] })
      setIsModalOpen(false)
      setSelectedVendor(null)
    }
  })

  // Update vendor mutation
  const updateVendorMutation = useMutation({
    mutationFn: async ({ vendorId, vendorData }: { vendorId: string; vendorData: any }) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const response = await fetch(
        getApiUrl(`api/enhanced-transaction-processing/vendors/${vendorId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          },
          body: JSON.stringify(vendorData)
        }
      )
      const data = await response.json()
      if (!data.success) {
        throw new Error(data.error || 'Failed to update vendor')
      }
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', firstCompanyId] })
      setIsModalOpen(false)
      setSelectedVendor(null)
    }
  })

  // Delete vendor mutation
  const deleteVendorMutation = useMutation({
    mutationFn: async (vendorId: string) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const response = await fetch(
        getApiUrl(`api/enhanced-transaction-processing/vendors/${vendorId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          }
        }
      )
      
      const data = await response.json()
      
      // Check if response is not successful
      if (!response.ok || !data.success) {
        // Extract error message from response
        const errorMessage = data.error || data.message || 'Failed to delete vendor'
        throw new Error(errorMessage)
      }
      
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', firstCompanyId] })
      setDeleteConfirm(null)
      toast({
        title: tSync("Success"),
        description: tSync("Vendor deleted successfully"),
        variant: "default",
      })
    },
    onError: (error: Error) => {
      // Show clear error message to user
      toast({
        title: tSync("Cannot Delete Vendor"),
        description: error.message || tSync("This vendor cannot be deleted because it is used in transactions. Please remove or reassign the transactions first."),
        variant: "destructive",
      })
      setDeleteConfirm(null)
    }
  })

  const handleCreate = () => {
    setSelectedVendor(null)
    setIsModalOpen(true)
  }

  const handleEdit = (vendor: Vendor) => {
    setSelectedVendor(vendor)
    setIsModalOpen(true)
  }

  const handleDelete = (vendorId: string) => {
    if (deleteConfirm === vendorId) {
      deleteVendorMutation.mutate(vendorId)
    } else {
      setDeleteConfirm(vendorId)
    }
  }

  const handleSubmit = async (vendorData: any) => {
    try {
      if (selectedVendor) {
        await updateVendorMutation.mutateAsync({
          vendorId: selectedVendor.id,
          vendorData
        })
      } else {
        await createVendorMutation.mutateAsync(vendorData)
      }
    } catch (error) {
      // Error is handled by the mutation, but we re-throw to keep modal open
      throw error
    }
  }

  return (
    <PageLayout>
      <div className="space-y-6 pr-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold"><TranslatedText text="Vendors & Suppliers" /></h1>
            <p className="text-muted-foreground mt-1">
              <TranslatedText text="Manage your vendors and suppliers" />
            </p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            <TranslatedText text="Add New Vendor" />
          </Button>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11 border border-slate-300"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vendors Table */}
        <Card>
          <CardHeader>
            <CardTitle><TranslatedText text="All Vendors" /></CardTitle>
            <CardDescription>
              {filteredVendors.length} <TranslatedText text={filteredVendors.length !== 1 ? "vendors" : "vendor"} /> <TranslatedText text="found" />
            </CardDescription>
          </CardHeader>
          <CardContent>
            {vendorsError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium"><TranslatedText text="Error loading vendors" /></span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  {vendorsError instanceof Error ? vendorsError.message : <TranslatedText text="Failed to fetch vendors" />}
                </p>
              </div>
            )}
            {vendorsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48 bg-gray-200" />
                      <Skeleton className="h-3 w-32 bg-gray-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredVendors.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchQuery ? <TranslatedText text="No vendors found" /> : <TranslatedText text="No vendors yet" />}
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery
                    ? <TranslatedText text="Try adjusting your search query" />
                    : <TranslatedText text="Get started by adding your first vendor" />}
                </p>
                {!searchQuery && (
                  <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="Add New Vendor" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead><TranslatedText text="Name" /></TableHead>
                      <TableHead><TranslatedText text="Contact" /></TableHead>
                      <TableHead><TranslatedText text="Address" /></TableHead>
                      <TableHead><TranslatedText text="Tax Number" /></TableHead>
                      <TableHead><TranslatedText text="Status" /></TableHead>
                      <TableHead className="text-right"><TranslatedText text="Actions" /></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-teal-100 rounded-lg">
                              <Building2 className="h-4 w-4 text-teal-600" />
                            </div>
                            <div>
                              <div className="font-medium">{vendor.name}</div>
                              {vendor.country && (
                                <div className="text-xs text-gray-500">{vendor.country}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {vendor.email && (
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-3 w-3 text-gray-400" />
                                {vendor.email}
                              </div>
                            )}
                            {vendor.phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="h-3 w-3 text-gray-400" />
                                {vendor.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {vendor.address ? (
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin className="h-3 w-3 text-gray-400 mt-0.5" />
                              <div>
                                <div>{vendor.address}</div>
                                {(vendor.city || vendor.state) && (
                                  <div className="text-xs text-gray-500">
                                    {[vendor.city, vendor.state, vendor.zipCode]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {vendor.taxNumber ? (
                            <span className="text-sm">{vendor.taxNumber}</span>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={vendor.isActive ? 'default' : 'secondary'}
                            className={vendor.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                          >
                            {vendor.isActive ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                <TranslatedText text="Active" />
                              </>
                            ) : (
                              <>
                                <X className="h-3 w-3 mr-1" />
                                <TranslatedText text="Inactive" />
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(vendor)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {deleteConfirm === vendor.id ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDelete(vendor.id)}
                                  disabled={deleteVendorMutation.isPending}
                                >
                                  <AlertCircle className="h-4 w-4 mr-1" />
                                  <TranslatedText text="Confirm" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeleteConfirm(null)}
                                >
                                  <TranslatedText text="Cancel" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(vendor.id)}
                                disabled={deleteVendorMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendor Modal */}
        <VendorModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedVendor(null)
          }}
          onSubmit={handleSubmit}
          vendor={selectedVendor || undefined}
          companyId={firstCompanyId}
          isLoading={createVendorMutation.isPending || updateVendorMutation.isPending}
        />
      </div>
    </PageLayout>
  )
}

